import type { Language } from '@/shared/i18n';

import {
  localizeBlogPost,
  type BlogPost,
  type BlogPostKind,
  type LocalizedBlogPost
} from './blogPosts';

export type BloggerImage = {
  altText: string;
  caption: string;
  fileName: string;
  id: string;
  isFeatured: boolean;
  kind: 'article' | 'featured';
  order: number;
  url: string;
};

export type BloggerPublicArticle = {
  author: {
    id: string;
    name: string;
    role: string;
  };
  contentHtml?: string;
  contentJson?: unknown;
  destinations: string[];
  excerpt: string;
  featuredImage: BloggerImage | null;
  id: string;
  images?: BloggerImage[];
  industry: string;
  publishDate: string;
  publishTime: string;
  publishedAt: string | null;
  seo: {
    allowIndexing: boolean;
    canonicalUrl: string;
    description: string;
    openGraphDescription: string;
    openGraphTitle: string;
    socialImageUrl: string;
    title: string;
  };
  service: string;
  slug: string;
  timezone: string;
  title: string;
  type: BlogPostKind;
  updatedAt: string;
  visibility: string;
};

type BloggerListResponse = {
  articles: BloggerPublicArticle[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
};

type BloggerDetailResponse = {
  article: BloggerPublicArticle;
};

const configuredApiOrigin = import.meta.env.VITE_BLOGGER_API_URL?.trim();
const apiOrigin = configuredApiOrigin?.replace(/\/$/, '') ?? '';
const publicContentPath = '/api/content';
const destination = 'datasliv';

const createApiUrl = (path: string) => `${apiOrigin}${path}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseJsonResponse = async <ResponseType>(response: Response) => {
  if (!response.ok) {
    throw new Error(
      `Blogger API request failed with status ${response.status}.`
    );
  }

  return (await response.json()) as ResponseType;
};

export const fetchBloggerArticles = async (language: Language) => {
  const search = new URLSearchParams({
    destination,
    lang: language,
    limit: '100'
  });
  const response = await fetch(
    createApiUrl(`${publicContentPath}?${search.toString()}`),
    {
      headers: {
        Accept: 'application/json'
      }
    }
  );
  const data = await parseJsonResponse<BloggerListResponse>(response);

  if (!isRecord(data) || !Array.isArray(data.articles)) {
    throw new Error('Blogger API returned an invalid article list.');
  }

  return data.articles;
};

const fetchBloggerArticleByType = async (
  type: BlogPostKind,
  slug: string,
  language: Language
) => {
  const search = new URLSearchParams({ destination, lang: language });
  const response = await fetch(
    createApiUrl(
      `${publicContentPath}/${type}/${encodeURIComponent(slug)}?${search.toString()}`
    ),
    {
      headers: {
        Accept: 'application/json'
      }
    }
  );

  if (response.status === 404) {
    return null;
  }

  const data = await parseJsonResponse<BloggerDetailResponse>(response);

  if (!isRecord(data) || !isRecord(data.article)) {
    throw new Error('Blogger API returned an invalid article.');
  }

  return data.article;
};

export const fetchBloggerArticle = async (slug: string, language: Language) => {
  const blogArticle = await fetchBloggerArticleByType('blog', slug, language);

  if (blogArticle) {
    return blogArticle;
  }

  return fetchBloggerArticleByType('news', slug, language);
};

const normalizeFilterKey = (value: string, fallback: string) => {
  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || fallback;
};

const toDateOnly = (value: string | null | undefined) => {
  const match = value?.match(/^\d{4}-\d{2}-\d{2}/);

  return match?.[0] ?? '';
};

const stripHtml = (value: string) =>
  value
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getReadTime = (article: BloggerPublicArticle, language: Language) => {
  const readableContent = stripHtml(
    article.contentHtml?.trim() || article.excerpt
  );
  const wordCount = readableContent ? readableContent.split(/\s+/).length : 0;
  const minutes = Math.max(1, Math.ceil(wordCount / 200));

  if (language === 'sr') {
    return `${minutes} min čitanja`;
  }

  if (language === 'es') {
    return `${minutes} min lectura`;
  }

  return `${minutes} min read`;
};

const createLocalizedLabels = (value: string) => ({
  sr: value,
  es: value,
  en: value
});

const createLocalizedContent = (
  article: BloggerPublicArticle,
  bodyText: string
) => ({
  sr: {
    bodyText,
    category: article.service || 'DataSliv',
    excerpt: article.excerpt,
    imageAlt: article.featuredImage?.altText || article.title,
    readTime: getReadTime(article, 'sr'),
    seoDescription: article.seo.description || article.excerpt,
    seoKeywords: [article.service, article.industry, 'DataSliv']
      .filter(Boolean)
      .join(', '),
    seoTitle: article.seo.title || article.title,
    title: article.title
  },
  es: {
    bodyText,
    category: article.service || 'DataSliv',
    excerpt: article.excerpt,
    imageAlt: article.featuredImage?.altText || article.title,
    readTime: getReadTime(article, 'es'),
    seoDescription: article.seo.description || article.excerpt,
    seoKeywords: [article.service, article.industry, 'DataSliv']
      .filter(Boolean)
      .join(', '),
    seoTitle: article.seo.title || article.title,
    title: article.title
  },
  en: {
    bodyText,
    category: article.service || 'DataSliv',
    excerpt: article.excerpt,
    imageAlt: article.featuredImage?.altText || article.title,
    readTime: getReadTime(article, 'en'),
    seoDescription: article.seo.description || article.excerpt,
    seoKeywords: [article.service, article.industry, 'DataSliv']
      .filter(Boolean)
      .join(', '),
    seoTitle: article.seo.title || article.title,
    title: article.title
  }
});

export const mapBloggerArticleToBlogPost = (
  article: BloggerPublicArticle
): BlogPost => {
  const service = article.service || 'DataSliv';
  const industry = article.industry || 'General';
  const publishedAt =
    toDateOnly(article.publishDate) ||
    toDateOnly(article.publishedAt) ||
    toDateOnly(article.updatedAt);
  const updatedAt = toDateOnly(article.updatedAt);
  const imageSrc =
    article.featuredImage?.url ||
    article.seo.socialImageUrl ||
    '/images/datasliv_dark.jpg';
  const articleImages = (article.images ?? [])
    .filter(image => image.kind === 'article' && !image.isFeatured && image.url)
    .sort((firstImage, secondImage) => firstImage.order - secondImage.order)
    .map(image => ({
      alt: image.altText || article.title,
      caption: image.caption || undefined,
      id: image.id,
      src: image.url
    }));

  return {
    authorName: article.author?.name || 'DataSliv team',
    authorRole: article.author?.role || undefined,
    articleImages,
    canonicalUrl: article.seo.canonicalUrl || undefined,
    content: createLocalizedContent(article, article.contentHtml ?? ''),
    imageCaption: article.featuredImage?.caption || undefined,
    imageSrc,
    industry: createLocalizedLabels(industry),
    industryKey: normalizeFilterKey(industry, 'general'),
    kind: article.type === 'news' ? 'news' : 'blog',
    publishedAt,
    service: createLocalizedLabels(service),
    serviceKey: normalizeFilterKey(service, 'datasliv'),
    slug: article.slug,
    updatedAt: updatedAt || undefined
  };
};

export const localizeBloggerArticle = (
  article: BloggerPublicArticle,
  language: Language
): LocalizedBlogPost =>
  localizeBlogPost(mapBloggerArticleToBlogPost(article), language);
